// =============================================================================
// src/features/explore/explore-g26-store.ts — Carregador (sob demanda, com cache)
//   dos assets das Eleicoes Gerais 2026 no Explorar.
//
// Os arquivos so sao baixados quando o usuario entra em "2026" (principal) ou liga
//   a camada de locais (indice + votos do cargo ativo). Falha de rede limpa o cache
//   da promessa p/ permitir nova tentativa. Fetch injetado (DI) — testes usam um
//   FakeG26Fetcher em vez de stub inline.
// =============================================================================

import type { G26CargoCode, G26Dataset, G26LocaisIndex, G26LocaisVotos } from './explore-g26-types';
import { G26_ASSET_URLS } from './explore-g26-constants';

/** Busca um JSON por URL (rejeita em HTTP != 2xx). */
export type G26JsonFetcher = (url: string) => Promise<unknown>;

/** True quando `value` e um objeto JSON (nao null/array). */
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Valida a forma minima do asset principal na FRONTEIRA (antes de virar estado):
 * uma resposta inesperada (HTML de erro, JSON de outro asset) nao pode chegar ao
 * render — derrubava o mapa (pego pela suite em 2026-10-13).
 *
 * @example parseG26Dataset(await res.json()).cargos[0].code
 */
export function parseG26Dataset(json: unknown): G26Dataset {
  const ok = isJsonObject(json) && Array.isArray(json.cargos) && Array.isArray(json.candidatos)
    && isJsonObject(json.estado) && isJsonObject(json.municipios) && isJsonObject(json.zonas);
  if (!ok) throw new Error(`asset 2026 invalido: esperado {cargos[], candidatos[], estado{}, municipios{}, zonas{}}, recebido ${JSON.stringify(json)?.slice(0, 120)}`);
  return json as unknown as G26Dataset;
}

/** Valida o indice de locais (`locais` array). */
export function parseG26Locais(json: unknown): G26LocaisIndex {
  if (!isJsonObject(json) || !Array.isArray(json.locais)) throw new Error(`asset de locais 2026 invalido: esperado {locais[]}, recebido ${JSON.stringify(json)?.slice(0, 120)}`);
  return json as unknown as G26LocaisIndex;
}

/** Valida os votos por local de um cargo (`votos` array). */
export function parseG26LocaisVotos(json: unknown): G26LocaisVotos {
  if (!isJsonObject(json) || !Array.isArray(json.votos)) throw new Error(`asset de votos por local 2026 invalido: esperado {votos[]}, recebido ${JSON.stringify(json)?.slice(0, 120)}`);
  return json as unknown as G26LocaisVotos;
}

/** Fetcher de producao: fetch nativo + erro com URL/status quando falha. */
export async function fetchG26Json(url: string): Promise<unknown> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`asset ${url} respondeu HTTP ${response.status} (esperado 2xx)`);
  return response.json();
}

/**
 * Cache das promessas dos assets 2026. `dataset()` sincrono devolve o principal
 * ja carregado (ou null) p/ os caminhos de render, que nao podem esperar.
 */
export class ExploreG26Store {
  private datasetPromise: Promise<G26Dataset> | null = null;
  private loaded: G26Dataset | null = null;
  private locaisPromise: Promise<G26LocaisIndex> | null = null;
  private readonly votosPromises = new Map<G26CargoCode, Promise<G26LocaisVotos>>();

  constructor(private readonly fetchJson: G26JsonFetcher) {}

  /** Asset principal (estado/municipios/zonas), memoizado. */
  loadDataset(): Promise<G26Dataset> {
    this.datasetPromise ??= this.cached(G26_ASSET_URLS.dataset, parseG26Dataset, () => { this.datasetPromise = null; })
      .then((ds) => { this.loaded = ds; return ds; });
    return this.datasetPromise;
  }

  /** Dataset ja resolvido (null antes do 1o load). */
  dataset(): G26Dataset | null {
    return this.loaded;
  }

  /** Indice dos locais de votacao (fichas + coordenadas), memoizado. */
  loadLocais(): Promise<G26LocaisIndex> {
    this.locaisPromise ??= this.cached(G26_ASSET_URLS.locais, parseG26Locais, () => { this.locaisPromise = null; });
    return this.locaisPromise;
  }

  /** Votos de um cargo por local, memoizado por cargo. */
  loadLocaisVotos(cargo: G26CargoCode): Promise<G26LocaisVotos> {
    const hit = this.votosPromises.get(cargo);
    if (hit) return hit;
    const promise = this.cached(G26_ASSET_URLS.locaisVotos(cargo), parseG26LocaisVotos, () => this.votosPromises.delete(cargo));
    this.votosPromises.set(cargo, promise);
    return promise;
  }

  /** Busca + valida a URL; em erro (rede OU forma) roda `forget` (limpa o cache) e propaga. */
  private cached<T>(url: string, parse: (json: unknown) => T, forget: () => void): Promise<T> {
    return this.fetchJson(url).then(parse).catch((error: unknown) => { forget(); throw error; });
  }
}

/** Store de producao do Explorar (fetch nativo). */
export const G26_STORE = new ExploreG26Store(fetchG26Json);
